/**
 * App-local shell layout: resolves the current user from this app's auth
 * (MSAL/Entra) and hands it to the shared, auth-agnostic <AppShell> from
 * @andavo/web-ui. The shell renders the active route via <Outlet>.
 *
 * Also composes the primary-nav model: the platform modules (Chat/CRM/Expense)
 * ROLE-GATED to the surfaces the user actually holds a role for, plus a
 * ROLE-GATED Admin item shown only when the SERVER-SIDE admin gate
 * (GET /api/admin/me via useAdminMe) returns { admin: true }. Both are UI
 * affordances only — they do NOT replace the fail-closed server enforcement on
 * each surface (cm-api deny-by-default for CRM; the AdminGuard on /admin). The
 * admin-me query is cached (staleTime), so reading it here reuses the same
 * result the guard uses — no extra request.
 *
 * Lives inside the protected tree, so ProtectedRoute has already gated on
 * isAuthenticated() — the user is non-null here. The defensive Navigate is a
 * belt-and-suspenders against a torn-down session between the gate and render.
 */
import { AppShell, type NavItem } from '@andavo/web-ui';
import { ShieldCheckIcon } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { matchPath, Navigate, useLocation } from 'react-router';
import { logout } from '@/auth/msal';
import { useStoredRoles } from '@/auth/useWhoami';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { useAdminMe } from '@/features/admin/data-access/useAdminMe';
import { fullBleedPaths } from '@/features/routes-registry';
import { gatePlatformNav } from './nav-gating';
import { platformNav } from './platform-nav';

/** Whether `pathname` is (inside) a route that asked for the full content area. */
export function isFullBleedPath(
  pathname: string,
  paths: readonly string[] = fullBleedPaths,
): boolean {
  return paths.some(
    (path) => matchPath({ path, end: false }, pathname) !== null,
  );
}

export function ShellLayout() {
  const user = useCurrentUser();
  const { pathname } = useLocation();
  // Server-side admin gate (cached). Only an explicit { admin: true } shows the
  // Admin rail item; pending/error/non-admin leave it hidden (fail-closed UI).
  const { data: adminMe } = useAdminMe();
  const isAdmin = adminMe?.admin === true;

  // The user's granted roles come from GET /api/whoami — the server's read of
  // experts.user_roles via get_user_roles() (SECURITY DEFINER, past RLS).
  //
  // THIS USED TO READ `idTokenClaims.roles` AND WAS ALWAYS UNDEFINED. The platform
  // deliberately keeps the JWT identity-only (rbac-model.json: "The JWT stays
  // IDENTITY-ONLY. Roles are NEVER read from the token"), so that claim is never
  // minted — gatePlatformNav therefore always took its fail-open branch and every
  // user saw every module. Observed 2026-08-10: Stacie held CRM + Hotel Supplier
  // and still saw Chat and Expense on the rail.
  //
  // Still fails open while the fetch is in flight or on error (undefined), because
  // nav is cosmetic and access is server-enforced: a dead-end tab is a smaller
  // problem than hiding a module someone is entitled to.
  const storedRoles = useStoredRoles();

  const navItems = useMemo<NavItem[]>(() => {
    const gated = gatePlatformNav(platformNav, storedRoles, isAdmin);
    return isAdmin
      ? [
          ...gated,
          { icon: <ShieldCheckIcon />, label: 'Admin', path: '/admin' },
        ]
      : gated;
  }, [isAdmin, storedRoles]);

  if (!user) {
    return <Navigate replace to="/signin" />;
  }
  return (
    <AppShell
      user={user}
      navItems={navItems}
      // App-style surfaces (chat) fill the content area; everything else keeps
      // the padded gutter. See features/routes-registry.ts fullBleedPaths.
      contentLayout={isFullBleedPath(pathname) ? 'full-bleed' : 'padded'}
      onSignOut={() => {
        void logout();
      }}
    />
  );
}
